import type { UserWithRole } from 'better-auth/plugins/admin';
import { Loader2 } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

import { UserActionsMenu } from './UserActionsMenu';

interface UsersTableProps {
  users?: UserWithRole[];
  roleOptions: string[];
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  error: unknown;
}

export function UsersTable({
  users,
  roleOptions,
  isLoading,
  isFetching,
  isError,
  error,
}: UsersTableProps) {
  const showEmptyState =
    !isLoading && !isFetching && (users?.length ?? 0) === 0;

  return (
    <div className="rounded-lg border border-neutral-200 bg-white shadow-sm">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead>Email</TableHead>
            <TableHead>Role</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Created</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {isLoading ? (
            Array.from({ length: 5 }).map((_, index) => (
              <TableRow key={`skeleton-${index}`}>
                <TableCell>
                  <Skeleton className="h-4 w-40" />
                </TableCell>
                <TableCell>
                  <Skeleton className="h-4 w-48" />
                </TableCell>
                <TableCell>
                  <Skeleton className="h-4 w-16" />
                </TableCell>
                <TableCell>
                  <Skeleton className="h-4 w-24" />
                </TableCell>
                <TableCell>
                  <Skeleton className="h-4 w-28" />
                </TableCell>
                <TableCell className="text-right">
                  <Skeleton className="ml-auto h-8 w-24" />
                </TableCell>
              </TableRow>
            ))
          ) : showEmptyState ? (
            <TableRow>
              <TableCell
                colSpan={6}
                className="py-10 text-center text-sm text-neutral-500">
                No users match your current filters.
              </TableCell>
            </TableRow>
          ) : (
            users?.map((user) => (
              <TableRow key={user.id}>
                <TableCell className="font-medium">
                  {user.name || '—'}
                </TableCell>
                <TableCell className="font-mono text-sm text-neutral-700">
                  {user.email}
                </TableCell>
                <TableCell>
                  <Badge variant="secondary" className="capitalize">
                    {user.role ?? 'user'}
                  </Badge>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    {user.banned ? (
                      <Badge variant="destructive">Banned</Badge>
                    ) : (
                      <Badge variant="outline">
                        {user.emailVerified ? 'Verified' : 'Unverified'}
                      </Badge>
                    )}
                  </div>
                </TableCell>
                <TableCell className="text-sm text-neutral-600">
                  {user.createdAt
                    ? new Date(user.createdAt).toLocaleDateString()
                    : '—'}
                </TableCell>
                <TableCell className="text-right">
                  <UserActionsMenu user={user} roleOptions={roleOptions} />
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
      {isFetching && !isLoading && (
        <div className="flex items-center gap-2 border-t border-neutral-200 bg-neutral-50 px-4 py-3 text-sm text-neutral-600">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
          Refreshing data…
        </div>
      )}
      {isError && (
        <div className="border-t border-neutral-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error instanceof Error ? error.message : 'Unable to load users.'}
        </div>
      )}
    </div>
  );
}
